import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Check } from "lucide-react";

import { Header } from "@/components/site/Header";
import { QuickQuoteStepLabel } from "@/components/forms/QuickQuoteSteps";
import { Footer } from "@/components/site/Footer";
import {
  US_STATES,
  clearLeadId,
  readLeadId,
  readStep1Norwood,
} from "@/components/forms/quick-quote-shared";
import { norwoodClasses } from "@/lib/site-data";
import {
  completeQuickQuote,
  deliverQuickQuoteWithoutPhoto,
  uploadQuickQuotePhotos,
} from "@/lib/forms/quick-quote.functions";
import { getTrackingParams } from "@/lib/utm-tracking";
import { QQ_STICKY_BAR_CLS, scrollToInvalid } from "@/components/forms/quick-quote-scroll";
import {
  looksLikeImage,
  newPhotoId,
  preparePhoto,
  uploadTimeoutMs,
  uploadWithRetry,
  type PreparedPhoto,
} from "@/components/forms/quick-quote-photos";
import stepIcon from "@/assets/restore/icon-step-1.webp";
import bookletImg from "@/assets/restore/BOOKLET.webp";

const TITLE = "Finish Your Quick Quote - RESTORE Hair";
const DESCRIPTION =
  "Add your address to finish your RESTORE Hair quick quote and get matched with your nearest hair restoration clinic.";
const CANONICAL_URL = "https://restorehair.com/quick-quote-page-2/";

export const Route = createFileRoute("/quick-quote-page-2")({
  validateSearch: (search: Record<string, unknown>) => ({
    qq: typeof search["qq"] === "string" ? (search["qq"] as string) : undefined,
  }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: CANONICAL_URL }],
  }),
  component: QuickQuotePage2,
});

/** Boxed field with the label sitting inside the top of the control. */
function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}) {
  // As on step 1: the row id lets a failed Next scroll the whole box
  // (label, control and message) into view, not only the input inside it.
  return (
    <div id={`${id}-row`}>
      <div
        className={`border bg-background px-4 pb-2 pt-2.5 ${
          error ? "border-destructive ring-1 ring-destructive" : "border-border"
        }`}
      >
        <label
          htmlFor={id}
          className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
        >
          {label}
        </label>
        {children}
      </div>
      {error && (
        <p id={errorId(id)} className="mt-1 text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function errorId(id: string) {
  return `${id}-error`;
}

// Inline messages for a Next tap that fails validation (18 Oct 2026). UI
// text only; which fields are required is unchanged.
const ADDRESS_MESSAGES: Record<string, string> = {
  qq_address1: "Please enter your street address.",
  qq_city: "Please enter your city.",
  qq_state: "Please choose your state.",
  qq_address_zip: "Please enter your zip code.",
};
const NORWOOD_MESSAGE = "Please tap the image that looks most like your hair loss.";
const NORWOOD_ROW_ID = "norwood-picker";
const PHOTO_ROW_ID = "photo-row";
const PHOTO_REQUIRED_MESSAGE = "Please select at least one photo or skip this step.";

type Validatable = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** Address controls the browser marks invalid, in page order. */
function findInvalidAddress(form: HTMLFormElement): Validatable[] {
  const out: Validatable[] = [];
  for (const node of Array.from(form.elements)) {
    if (
      (node instanceof HTMLInputElement ||
        node instanceof HTMLSelectElement ||
        node instanceof HTMLTextAreaElement) &&
      node.willValidate &&
      !node.validity.valid
    ) {
      out.push(node);
    }
  }
  return out;
}

const controlCls =
  "scroll-mb-40 scroll-mt-36 w-full border-0 bg-transparent p-0 pt-1.5 pb-1 text-base text-foreground outline-none focus:ring-0";

const ACUITY_OWNER = "24213130";
const ACUITY_APPOINTMENTS = [
  { label: "Charlotte, NC", type: "49645045" },
  { label: "Denver, CO", type: "49646910" },
  { label: "Boulder, CO", type: "49646889" },
  { label: "Greenwood Village, CO", type: "49646857" },
  { label: "Manhattan, NY", type: "49646097" },
  { label: "Oak Brook, IL", type: "49647719" },
  { label: "Plano, TX", type: "49647474" },
  { label: "Virtual Consultation", type: "26951951" },
];

function AcuityButton({ label, type }: { label: string; type: string }) {
  const href = `https://app.acuityscheduling.com/schedule.php?owner=${ACUITY_OWNER}&appointmentType=${type}`;
  return (
    <a
      href={href}
      className="block border border-border bg-background px-4 py-3 text-center text-sm font-semibold text-foreground transition hover:bg-muted"
    >
      {label}
    </a>
  );
}

/**
 * Fire a Zaraz funnel-step event (no personal data — step name and photo
 * count only). Best-effort: tracking must never block the quote flow.
 */
function trackZaraz(event: string, props?: Record<string, number>) {
  try {
    const zaraz = (window as unknown as { zaraz?: { track?: (n: string, p?: unknown) => void } }).zaraz;
    zaraz?.track?.(event, props);
  } catch {
    /* ignore */
  }
}

type PhotoStatus = "ready" | "uploading" | "uploaded" | "failed";

/** One selected photo. `id` is sent with every try, so a retry is idempotent. */
interface SelectedPhoto {
  id: string;
  file: File;
  url: string;
  status: PhotoStatus;
  /** Upload tries sent so far, automatic and manual. */
  attempts: number;
}

const UPLOAD_FAILED_MESSAGE =
  "We couldn't upload those photos. Please try again with fewer photos, or choose \u201CSkip Photo & Finish\u201D and we'll collect them on your call.";

/** Technical upload failures (storage errors) are logged, never shown raw. */
function friendlyUploadError(err: unknown): string {
  const raw = err instanceof Error ? err.message : "";
  return raw && !/failed \[|Google|fetch|network error|load failed|abort|timed out/i.test(raw)
    ? raw
    : UPLOAD_FAILED_MESSAGE;
}

function QuickQuotePage2() {
  const navigate = useNavigate();
  const complete = useServerFn(completeQuickQuote);
  const uploadPhotos = useServerFn(uploadQuickQuotePhotos);
  const skipPhoto = useServerFn(deliverQuickQuoteWithoutPhoto);

  const MAX_PHOTOS = 6;

  const [leadId, setLeadId] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [step, setStep] = useState<"address" | "photo" | "done">("address");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Hair-loss class (16 Oct 2026). Optional on step 1; when step 1 was sent
  // without one, the picker is shown here and the address cannot be sent
  // until a class is chosen. A class chosen on step 1 is re-sent with the
  // address, and this page then looks as it did before.
  const [askNorwood, setAskNorwood] = useState(false);
  const [norwood, setNorwood] = useState("");
  const [norwoodError, setNorwoodError] = useState(false);
  const pickerRef = useRef<HTMLFieldSetElement>(null);
  const [photos, setPhotos] = useState<SelectedPhoto[]>([]);
  // The upload loop reads the latest photo list from here, not from a render.
  const photosRef = useRef<SelectedPhoto[]>([]);
  // Prepared JPEGs, kept until their upload succeeds, so a retry does not
  // decode or convert the photo again.
  const preparedRef = useRef(new Map<string, PreparedPhoto>());
  const [photoCount, setPhotoCount] = useState(0);
  const [progress, setProgress] = useState<string | null>(null);
  // Validation, as on step 1 (18 Oct 2026). Address errors keyed by control
  // name; photoError is the photo field's own message (none chosen, wrong
  // file, upload failed). error stays for messages about the send itself,
  // shown in the button bar.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [photoError, setPhotoError] = useState<string | null>(null);
  // After a failed Next or Upload: scroll the row once (and focus the
  // control on desktop) after the red borders render.
  const [scrollRequest, setScrollRequest] = useState<{
    rowId: string;
    controlId: string;
    n: number;
  } | null>(null);
  // Touch screens get no focus, so a screen reader hears the first problem
  // from this polite live region instead.
  const [liveMessage, setLiveMessage] = useState("");
  const barRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!scrollRequest) return;
    const row = document.getElementById(scrollRequest.rowId);
    if (!row) return;
    scrollToInvalid(row, document.getElementById(scrollRequest.controlId), null, barRef.current);
  }, [scrollRequest]);

  function requestScroll(rowId: string, controlId: string) {
    setScrollRequest((prev) => ({ rowId, controlId, n: (prev?.n ?? 0) + 1 }));
  }

  /** Clear an address field's message once its value is valid. */
  function clearFieldError(target: EventTarget | null) {
    const el = target as Validatable | null;
    const name = el?.name;
    if (!el || !name || !(name in fieldErrors) || !el.validity.valid) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  function errorProps(name: string) {
    return fieldErrors[name]
      ? { "aria-invalid": true as const, "aria-describedby": errorId(name) }
      : {};
  }

  const [addr, setAddr] = useState({
    address1: "",
    address2: "",
    city: "",
    state: "",
    address_zip: "",
  });
  const setAddrField = (key: keyof typeof addr) => (value: string) =>
    setAddr((prev) => ({ ...prev, [key]: value }));

  // Some third-party scripts (tag manager / form trackers / autofill helpers)
  // blank out address inputs after we render. Re-assert React's value onto the
  // DOM node on every render so anything that wipes a field is undone.
  //
  // Only a blanked field is restored. A field that holds a value React has
  // not seen yet was filled by the browser's address autofill, which writes
  // several fields at once: putting React's older value back would erase
  // them. Those values are taken into state instead.
  const addrRefs = useRef<Record<string, HTMLInputElement | HTMLSelectElement | null>>({});
  useEffect(() => {
    const adopted: Partial<typeof addr> = {};
    for (const [key, value] of Object.entries(addr) as [keyof typeof addr, string][]) {
      const el = addrRefs.current[key];
      if (!el || el.value === value) continue;
      if (el.value === "") el.value = value;
      else adopted[key] = el.value;
    }
    if (Object.keys(adopted).length) setAddr((prev) => ({ ...prev, ...adopted }));
  });

  useEffect(() => {
    const id = readLeadId();
    const step1 = readStep1Norwood(id);
    setLeadId(id);
    setAskNorwood(step1.ask);
    setNorwood(step1.norwood);
    setResolved(true);
  }, []);

  async function onAddressSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!leadId || submitting) return;
    // Every problem at once, in page order: the class picker (when step 1
    // had no class), then each empty address field. One scroll to the first.
    const missingNorwood = askNorwood && !norwood;
    const invalid = findInvalidAddress(e.currentTarget);
    if (missingNorwood || invalid.length > 0) {
      const errors: Record<string, string> = {};
      for (const el of invalid)
        errors[el.name] = ADDRESS_MESSAGES[el.name] ?? "Please fill out this field.";
      setFieldErrors(errors);
      setNorwoodError(missingNorwood);
      setError(
        missingNorwood
          ? "One more thing: choose the image that looks most like your hair loss, at the top of the form."
          : null,
      );
      if (missingNorwood) {
        // The picker's own message is role="alert" already.
        setLiveMessage("");
        requestScroll(NORWOOD_ROW_ID, "norwood-0");
      } else {
        const first = invalid[0]!;
        setLiveMessage(errors[first.name] ?? "");
        requestScroll(`${first.id}-row`, first.id);
      }
      return;
    }
    setFieldErrors({});
    setLiveMessage("");
    setSubmitting(true);
    setError(null);
    try {
      const cityValue = addr.city.trim();
      const stateValue = addr.state.trim();
      const addressZip = addr.address_zip.trim();
      await complete({
        data: {
          lead_id: leadId,
          address1: addr.address1.trim(),
          address2: addr.address2.trim() || undefined,
          city: cityValue,
          state: stateValue,
          address_zip: addressZip,
          norwood: norwood || undefined,
          tracking: getTrackingParams(),
        },
      });
      trackZaraz("qq_address_saved");
      setStep("photo");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function commitPhotos(next: SelectedPhoto[]) {
    photosRef.current = next;
    setPhotos(next);
  }

  function patchPhoto(id: string, patch: Partial<SelectedPhoto>) {
    commitPhotos(photosRef.current.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }

  function finishWithPhotos() {
    const uploaded = photosRef.current.filter((p) => p.status === "uploaded").length;
    setPhotoCount(uploaded);
    trackZaraz("qq_photo_uploaded", { photos: uploaded });
    clearLeadId();
    navigate({ to: "/contact-us/confirmation-2s" });
  }

  /**
   * Prepare and upload one photo, with up to 3 tries. Returns the error when
   * it failed; the photo then shows "Failed" with a Retry button.
   */
  async function uploadOne(photo: SelectedPhoto, deliver: boolean): Promise<unknown> {
    if (!leadId) return new Error("We couldn't find your quote. Please start again.");
    const all = photosRef.current;
    const position = all.findIndex((p) => p.id === photo.id) + 1;
    const total = all.length;
    patchPhoto(photo.id, { status: "uploading" });
    setProgress(`Uploading photo ${position} of ${total}\u2026`);
    try {
      let prepared = preparedRef.current.get(photo.id);
      if (!prepared) {
        prepared = await preparePhoto(photo.file);
        preparedRef.current.set(photo.id, prepared);
        if (prepared.heic && prepared.via !== "original") {
          // Counts only: no file name, size or content.
          trackZaraz("qq_photo_converted", { library: prepared.via === "library" ? 1 : 0 });
        }
      }
      const { base64, mime } = prepared;
      await uploadWithRetry(
        (attempt, signal) => {
          patchPhoto(photo.id, { attempts: attempt });
          return uploadPhotos({
            data: {
              lead_id: leadId,
              photos: [{ photo_base64: base64, photo_mime: mime, photo_id: photo.id, attempt }],
              tracking: getTrackingParams(),
              // Only the final photo sends the lead on, so multiple photos
              // still create a single record.
              deliver,
            },
            signal,
          });
        },
        uploadTimeoutMs(base64.length),
        (attempt) => {
          setProgress(`Retrying photo ${position} of ${total}\u2026`);
          trackZaraz("qq_photo_retry", { attempt });
        },
        photo.attempts + 1,
      );
      preparedRef.current.delete(photo.id);
      patchPhoto(photo.id, { status: "uploaded" });
      return null;
    } catch (err) {
      console.error("[quickQuote] photo upload failed", err);
      patchPhoto(photo.id, { status: "failed" });
      return err;
    }
  }

  /**
   * Upload one photo per request. A single request carrying every photo is
   * too large for flaky mobile connections and fails as "Load failed".
   * Photos already on the server are kept and never sent again.
   */
  async function uploadPhotoList(targets: SelectedPhoto[]) {
    if (!leadId || submitting) return;
    setSubmitting(true);
    setError(null);
    setPhotoError(null);
    setLiveMessage("");
    let lastError: unknown = null;
    try {
      if (targets.length === 0) {
        // Every photo is up; only the final delivery is left to do.
        await skipPhoto({ data: { lead_id: leadId, tracking: getTrackingParams() } });
      }
      for (let i = 0; i < targets.length; i += 1) {
        const err = await uploadOne(targets[i]!, i === targets.length - 1);
        if (err) lastError = err;
      }
      const current = photosRef.current;
      if (current.every((p) => p.status === "uploaded")) {
        finishWithPhotos();
        return;
      }
      const kept = current.filter((p) => p.status === "uploaded").length;
      const friendly = friendlyUploadError(lastError);
      showPhotoError(
        kept > 0 ? `${kept} of ${current.length} photos uploaded. ${friendly}` : friendly,
        true,
      );
    } catch (err) {
      console.error("[quickQuote] photo upload failed", err);
      showPhotoError(friendlyUploadError(err), true);
    } finally {
      setProgress(null);
      setSubmitting(false);
    }
  }

  async function onPhotoSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!leadId || submitting) return;
    if (photosRef.current.length === 0) {
      showPhotoError(PHOTO_REQUIRED_MESSAGE, true);
      return;
    }
    await uploadPhotoList(photosRef.current.filter((p) => p.status !== "uploaded"));
  }

  /**
   * The photo field's message: red box, inline text, and after a tap on
   * Upload one scroll to the field (a pick of a wrong file does not scroll:
   * the visitor is at the field already).
   */
  function showPhotoError(message: string, scroll: boolean) {
    setPhotoError(message);
    setLiveMessage(message);
    if (scroll) requestScroll(PHOTO_ROW_ID, "photo");
  }

  function onRetryPhoto(id: string) {
    const photo = photosRef.current.find((p) => p.id === id);
    if (!photo || photo.status !== "failed") return;
    trackZaraz("qq_photo_retry", { attempt: photo.attempts + 1 });
    void uploadPhotoList([photo]);
  }

  async function onSkipPhoto() {
    if (!leadId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await skipPhoto({ data: { lead_id: leadId, tracking: getTrackingParams() } });
      trackZaraz("qq_photo_skipped");
      clearLeadId();
      navigate({ to: "/contact-us/confirmation-2s" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function addFiles(files: File[]) {
    setError(null);
    setPhotoError(null);
    const accepted: File[] = [];
    for (const file of files) {
      if (!looksLikeImage(file)) {
        showPhotoError("Please choose image files only (JPG, PNG, HEIC, etc.).", false);
        continue;
      }
      if (file.size > 20 * 1024 * 1024) {
        showPhotoError("Each image must be smaller than 20 MB.", false);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;
    const prev = photosRef.current;
    const room = MAX_PHOTOS - prev.length;
    if (room <= 0) {
      showPhotoError(`You can upload up to ${MAX_PHOTOS} photos.`, false);
      return;
    }
    const added = accepted.slice(0, room);
    if (accepted.length > room) {
      showPhotoError(
        `You can upload up to ${MAX_PHOTOS} photos — the extra files were not added.`,
        false,
      );
    }
    commitPhotos([
      ...prev,
      ...added.map((file) => ({
        id: newPhotoId(),
        file,
        url: URL.createObjectURL(file),
        status: "ready" as const,
        attempts: 0,
      })),
    ]);
  }

  function onFilesChange(list: FileList | null) {
    if (!list || list.length === 0) return;
    addFiles(Array.from(list));
  }

  function removeFile(id: string) {
    const photo = photosRef.current.find((p) => p.id === id);
    // A photo on the server, or on its way there, stays.
    if (!photo || photo.status === "uploaded" || photo.status === "uploading") return;
    URL.revokeObjectURL(photo.url);
    preparedRef.current.delete(id);
    commitPhotos(photosRef.current.filter((p) => p.id !== id));
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-10 lg:px-8 md:py-16">
        {resolved && !leadId ? (
          <div className="mx-auto max-w-3xl border border-border bg-background p-6 md:p-8">
            <h2 className="text-xl font-extrabold text-foreground">
              Let&apos;s start with your details
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              We couldn&apos;t find your quote in progress. Please complete the first step.
            </p>
            <a
              href="/quick-quote-part-1/"
              className="mt-5 inline-block bg-primary px-6 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90"
            >
              Start my quick quote
            </a>
          </div>
        ) : (
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Left — confirmation column */}
            <div>
              <div className="flex items-center gap-4">
                <img
                  src={stepIcon}
                  alt=""
                  width={64}
                  height={64}
                  loading="eager"
                  className="h-16 w-16 object-contain"
                />
                <h1 className="text-4xl font-light leading-tight tracking-tight text-foreground md:text-5xl">
                  Almost <span className="font-extrabold">There!</span>
                </h1>
              </div>
              <p className="mt-6 max-w-md text-base leading-relaxed text-foreground">
                Thanks for requesting a quick quote. Someone on our staff will call you very
                shortly to help deliver pricing information and to answer any additional
                questions.
              </p>

              {/* On phones the booklet pushed the step (and its progress bar)
                  below the fold, so there it is shown under the step, as
                  step 1 does with its testimonial (18 Oct 2026). */}
              <img
                src={bookletImg}
                alt="RESTORE VIP information package"
                width={420}
                height={420}
                loading="lazy"
                className="mt-10 hidden max-w-full object-contain lg:block"
              />
            </div>

            {/* Right — step content */}
            <div>
              <p className="sr-only" aria-live="polite">
                {liveMessage}
              </p>
              {step === "address" && (
                <>
                  <QuickQuoteStepLabel current={2} label="Your address" />
                  <h2 className="mt-8 text-2xl font-light text-foreground md:text-3xl">
                    Where can we send your VIP Package
                  </h2>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Enter your address below and we&apos;ll send you more information on our hair
                    restoration processes and special offers reserved for VIPs.
                  </p>

                  <form
                    onSubmit={onAddressSubmit}
                    // The page checks the fields itself after hydration (red
                    // box and message per field, as on step 1). Before that,
                    // the browser's own check still applies.
                    noValidate={resolved}
                    onInput={(e) => clearFieldError(e.target)}
                    onChange={(e) => clearFieldError(e.target)}
                    className="mt-6 space-y-4"
                  >
                    {askNorwood && (
                      <fieldset
                        id={NORWOOD_ROW_ID}
                        ref={pickerRef}
                        aria-invalid={norwoodError ? true : undefined}
                        aria-describedby={norwoodError ? "norwood-error" : undefined}
                        className={`scroll-mt-24 ${norwoodError ? "border-2 border-destructive p-3" : ""}`}
                      >
                        <legend className="text-base font-light text-foreground md:text-lg">
                          Which image most closely{" "}
                          <span className="whitespace-nowrap">
                            <span className="font-extrabold">resembles your hair loss?</span>
                            <span className="text-destructive"> *</span>
                          </span>
                        </legend>
                        {norwoodError && (
                          <p id="norwood-error" role="alert" className="mt-2 text-sm font-semibold text-destructive">
                            {NORWOOD_MESSAGE}
                          </p>
                        )}
                        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                          {norwoodClasses.map((c, i) => {
                            const selected = norwood === c.label;
                            return (
                              <button
                                type="button"
                                id={`norwood-${i}`}
                                key={c.label}
                                aria-pressed={selected}
                                onClick={() => {
                                  setNorwood(c.label);
                                  setNorwoodError(false);
                                  setError(null);
                                }}
                                className={`group relative flex flex-col items-center border-2 bg-background p-3 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${
                                  selected
                                    ? "border-primary shadow-lg"
                                    : "border-border hover:border-primary hover:bg-primary/5"
                                }`}
                              >
                                <span
                                  className={`absolute -top-3 left-1/2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border-2 transition ${
                                    selected
                                      ? "border-primary bg-primary text-primary-foreground"
                                      : "border-muted bg-background text-muted-foreground"
                                  }`}
                                >
                                  <Check className="h-3 w-3" strokeWidth={3} />
                                </span>
                                <img
                                  src={c.img}
                                  alt={`Norwood ${c.label} hair loss illustration`}
                                  width={204}
                                  height={130}
                                  loading="lazy"
                                  className="mt-3 h-20 w-auto object-contain"
                                />
                                <span className="mt-2 text-sm font-bold text-foreground transition-colors group-hover:text-primary">
                                  {c.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </fieldset>
                    )}
                    <Field id="qq_address1" error={fieldErrors.qq_address1} label="Address 1 *">
                      <input
                        className={controlCls}
                        id="qq_address1"
                        name="qq_address1"
                        {...errorProps("qq_address1")}
                        ref={(el) => { addrRefs.current.address1 = el; }}
                        required
                        maxLength={255}
                        autoComplete="address-line1"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        value={addr.address1}
                        onChange={(e) => setAddrField("address1")(e.target.value)}
                      />
                    </Field>
                    <Field id="qq_address2" label="Address 2">
                      <input
                        className={controlCls}
                        id="qq_address2"
                        name="qq_address2"
                        ref={(el) => { addrRefs.current.address2 = el; }}
                        maxLength={255}
                        autoComplete="address-line2"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        value={addr.address2}
                        onChange={(e) => setAddrField("address2")(e.target.value)}
                      />
                    </Field>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field id="qq_city" error={fieldErrors.qq_city} label="City *">
                        <input
                          className={controlCls}
                          id="qq_city"
                          name="qq_city"
                          {...errorProps("qq_city")}
                          ref={(el) => { addrRefs.current.city = el; }}
                          required
                          maxLength={80}
                          autoComplete="address-level2"
                          data-lpignore="true"
                          data-1p-ignore="true"
                          value={addr.city}
                          onChange={(e) => setAddrField("city")(e.target.value)}
                        />
                      </Field>
                      <Field id="qq_state" error={fieldErrors.qq_state} label="State *">
                        <select
                          className={controlCls}
                          id="qq_state"
                          name="qq_state"
                          {...errorProps("qq_state")}
                          ref={(el) => { addrRefs.current.state = el; }}
                          required
                          autoComplete="address-level1"
                          value={addr.state}
                          onChange={(e) => setAddrField("state")(e.target.value)}
                        >
                          <option value="" disabled>
                            Select a state
                          </option>
                          {US_STATES.map((state: string) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <Field
                      id="qq_address_zip"
                      error={fieldErrors.qq_address_zip}
                      label="Zip Code *"
                    >
                      <input
                        className={controlCls}
                        id="qq_address_zip"
                        name="qq_address_zip"
                        {...errorProps("qq_address_zip")}
                        inputMode="numeric"
                        ref={(el) => { addrRefs.current.address_zip = el; }}
                        required
                        maxLength={20}
                        autoComplete="postal-code"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        value={addr.address_zip}
                        onChange={(e) => setAddrField("address_zip")(e.target.value)}
                      />
                    </Field>

                    {/* Honeypot */}
                    <div className="sr-only" aria-hidden="true">
                      <label htmlFor="website">If you are human, leave this field blank.</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {/* Pinned to the bottom of the screen on phones, as on step 1. */}
                    <div ref={barRef} className={QQ_STICKY_BAR_CLS}>
                      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

                      <button
                        type="submit"
                        disabled={submitting || !resolved}
                        className="w-full bg-primary px-6 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {submitting ? "Sending…" : "Next Step"}
                      </button>
                    </div>
                  </form>
                </>
              )}

              {step === "photo" && (
                <>
                  <QuickQuoteStepLabel current={3} label="Your photos" />
                  <h2 className="mt-8 text-2xl font-light text-foreground md:text-3xl">
                    Last Step!
                  </h2>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Upload a few photos of your hair from different angles. This helps our
                    specialists prepare a more accurate quote before we call.
                  </p>

                  <form onSubmit={onPhotoSubmit} className="mt-6 space-y-5">
                    <div id={PHOTO_ROW_ID} className="space-y-5">
                      <label
                        htmlFor="photo"
                        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-muted/30 px-6 py-10 text-center transition hover:bg-muted/50 ${
                          photoError
                            ? "border-destructive ring-1 ring-destructive"
                            : "border-border"
                        }`}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          onFilesChange(e.dataTransfer.files);
                        }}
                      >
                        <span className="text-4xl" aria-hidden="true">
                          📷
                        </span>
                        <span className="mt-3 text-sm font-semibold text-foreground">
                          Drag &amp; drop your photos here
                        </span>
                        <span className="mt-1 text-xs text-muted-foreground">
                          or click to browse — up to {MAX_PHOTOS} photos (JPG, PNG, HEIC up to 20 MB
                          each)
                        </span>
                        <input
                          id="photo"
                          name="photo"
                          type="file"
                          multiple
                          accept="image/*,.heic,.heif,.jpg,.jpeg,.png,.webp"
                          aria-invalid={photoError ? true : undefined}
                          aria-describedby={photoError ? errorId("photo") : undefined}
                          className="sr-only"
                          onChange={(e) => {
                            onFilesChange(e.target.files);
                            e.target.value = "";
                          }}
                        />
                      </label>

                      {photos.length > 0 && (
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {photos.length} of {MAX_PHOTOS} photos selected
                          </p>
                          <div className="grid grid-cols-3 gap-3">
                            {photos.map((photo, index) => (
                              <div
                                key={photo.id}
                                className="relative"
                                data-photo-status={photo.status}
                              >
                                <img
                                  src={photo.url}
                                  alt={`Selected photo ${index + 1}`}
                                  className={`h-24 w-full rounded border object-cover ${
                                    photo.status === "failed"
                                      ? "border-destructive"
                                      : "border-border"
                                  } ${photo.status === "uploading" ? "opacity-60" : ""}`}
                                />
                                {photo.status === "uploading" && (
                                  <span className="absolute bottom-1 left-1 rounded bg-foreground/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-background">
                                    Uploading…
                                  </span>
                                )}
                                {photo.status === "uploaded" && (
                                  <span className="absolute bottom-1 left-1 rounded bg-foreground/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-background">
                                    Uploaded
                                  </span>
                                )}
                                {photo.status === "failed" && (
                                  <button
                                    type="button"
                                    onClick={() => onRetryPhoto(photo.id)}
                                    disabled={submitting}
                                    aria-label={`Retry photo ${index + 1}`}
                                    className="absolute bottom-1 left-1 rounded bg-destructive px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-destructive-foreground disabled:opacity-50"
                                  >
                                    Failed · Retry
                                  </button>
                                )}
                                {(photo.status === "ready" || photo.status === "failed") && (
                                  <button
                                    type="button"
                                    onClick={() => removeFile(photo.id)}
                                    disabled={submitting}
                                    aria-label={`Remove photo ${index + 1}`}
                                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-xs font-bold text-background transition hover:bg-foreground/80"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {photoError && (
                        <p id={errorId("photo")} className="text-sm text-destructive">
                          {photoError}
                        </p>
                      )}
                    </div>

                    {/* Pinned to the bottom of the screen on phones, as on step 1.
                        Upload is enabled with no photo chosen, so a tap says
                        what is missing instead of doing nothing. */}
                    <div ref={barRef} className={`${QQ_STICKY_BAR_CLS} space-y-3`}>
                      {error && <p className="text-sm text-destructive">{error}</p>}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-primary px-6 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {submitting
                          ? (progress ?? "Uploading…")
                          : photos.length > 1
                            ? `Upload ${photos.length} Photos & Finish`
                            : "Upload Photo & Finish"}
                      </button>
                      <button
                        type="button"
                        onClick={onSkipPhoto}
                        disabled={submitting}
                        className="w-full border border-border bg-background px-6 py-4 text-sm font-bold uppercase tracking-wider text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Skip Photo & Finish
                      </button>
                    </div>
                  </form>
                </>
              )}

              {step === "done" && (
                <div className="border border-border bg-background p-6 md:p-8">
                  <h2 className="text-2xl font-light text-foreground md:text-3xl">
                    Thank You!
                  </h2>
                  <p className="mt-3 text-base leading-relaxed text-foreground">
                    Your quick quote has been submitted. A RESTORE® specialist will reach out
                    shortly with your personalized quote.
                  </p>
                  {photoCount > 0 && (
                    <p className="mt-3 text-sm text-muted-foreground">
                      {photoCount === 1
                        ? "Your photo was uploaded successfully."
                        : `Your ${photoCount} photos were uploaded successfully.`}
                    </p>
                  )}

                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-foreground">
                      Schedule your complimentary consultation
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Select a convenient location to book your appointment:
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {ACUITY_APPOINTMENTS.map((appt) => (
                        <AcuityButton key={appt.type} label={appt.label} type={appt.type} />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Phones only: the booklet from the left column, under the step. */}
              {step !== "done" && (
                <img
                  src={bookletImg}
                  alt="RESTORE VIP information package"
                  width={420}
                  height={420}
                  loading="lazy"
                  className="mx-auto mt-10 max-w-full object-contain lg:hidden"
                />
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
